export type BrandColorEntry = {
  name: string;
  hex: string;
  semantic: string;
  semanticTextClass: string;
  semanticBgClass: string;
};

export type NeutralColorEntry = {
  name: string;
  hex: string;
  usage: string;
  bgClass: string;
  needsBorder?: boolean;
};

export type ColorRule = {
  number: string;
  title: string;
  description: string;
  bgClass: string;
  labelClass: string;
};

export type ProductLayerMapping = {
  layer: string;
  colorName: string;
  hex: string;
  layerBg: string;
  layerTextClass: string;
  usage: string;
};

export type AccentColor = {
  name: string;
  hex: string;
  note?: string;
};

export const heroColor: BrandColorEntry = {
  name: "Green",
  hex: "#A3E635",
  semantic: "PROTOCOL ★",
  semanticTextClass: "text-[#A3E635]",
  semanticBgClass: "bg-black",
};

export const gridColors: BrandColorEntry[] = [
  { name: "Blue", hex: "#3B82F6", semantic: "CLIENT", semanticTextClass: "text-white", semanticBgClass: "bg-[#3B82F6]" },
  { name: "Teal", hex: "#14B8A6", semantic: "CALLER", semanticTextClass: "text-white", semanticBgClass: "bg-[#14B8A6]" },
  { name: "Orange", hex: "#F97316", semantic: "RESPONDER", semanticTextClass: "text-white", semanticBgClass: "bg-[#F97316]" },
  { name: "Purple", hex: "#8B5CF6", semantic: "PLATFORM", semanticTextClass: "text-white", semanticBgClass: "bg-[#8B5CF6]" },
  { name: "Yellow", hex: "#FACC15", semantic: "SELFHOST", semanticTextClass: "text-black", semanticBgClass: "bg-[#FACC15]" },
  { name: "Indigo", hex: "#6366F1", semantic: "ACCENT", semanticTextClass: "text-white", semanticBgClass: "bg-[#6366F1]" },
  { name: "Pink", hex: "#EC4899", semantic: "HOTLINE", semanticTextClass: "text-white", semanticBgClass: "bg-[#EC4899]" },
  { name: "Red", hex: "#EF4444", semantic: "DESTRUCTIVE", semanticTextClass: "text-white", semanticBgClass: "bg-[#EF4444]" },
];

export const neutralColors: NeutralColorEntry[] = [
  { name: "Ink", hex: "#111111", usage: "边框 / 阴影 / 深底", bgClass: "bg-[#111111]" },
  { name: "White", hex: "#FFFFFF", usage: "光底 / 白卡", bgClass: "bg-white", needsBorder: true },
  { name: "Paper", hex: "#F7F2E8", usage: "页面底层背景", bgClass: "bg-[#F7F2E8]" },
  { name: "Paper Soft", hex: "#FCFAF3", usage: "次级浅底区域", bgClass: "bg-[#FCFAF3]" },
];

export const colorRules: ColorRule[] = [
  {
    number: "01",
    title: "品牌绿是协议锚点色",
    description: "Green（#A3E635）是全产品线最高层级的颜色。用于 Protocol 层，也是阴影高亮与 CTA 主按钮的标准色。不允许被其他产品层随意挪用。",
    bgClass: "bg-[#A3E635] text-black",
    labelClass: "text-black/50",
  },
  {
    number: "02",
    title: "品牌色只允许实色使用",
    description: "用于 SolidBlock (C.2) 时，9 个品牌色一律不带透明度。用于 GlassCard (C.1) 时，允许附加 /75 玻璃层（如 bg-[#8B5CF6]/75），使底层背景网格隐约可见。",
    bgClass: "bg-black text-white",
    labelClass: "text-white/30",
  },
  {
    number: "03",
    title: "语义色词优先",
    description: "选色时，先问语义——当前内容属于哪个产品层（Protocol/Client/Caller/Responder/Platform/Selfhost）？然后按语义分配对应色词。装饰性场景再从剩余色词中取用。",
    bgClass: "bg-[#6366F1] text-white",
    labelClass: "text-white/40",
  },
  {
    number: "04",
    title: "Ink 黑是绝对支撑色",
    description: "所有组件的边框、实体阴影、排版强调，统一使用 Ink（#111111/black）。禁止用其他深色代替。这是视觉统一的核心约束。",
    bgClass: "bg-[#111111] text-white",
    labelClass: "text-white/30",
  },
];

export const productLayerMappings: ProductLayerMapping[] = [
  { layer: "PROTOCOL", colorName: "Green", hex: "#A3E635", layerBg: "bg-[#A3E635]", layerTextClass: "text-black", usage: "协议层说明、调用合约、规范文档、Hotline 核心标识" },
  { layer: "CLIENT", colorName: "Blue", hex: "#3B82F6", layerBg: "bg-[#3B82F6]", layerTextClass: "text-white", usage: "Client 端整体标识、SDK 入口、客户端文档" },
  { layer: "CALLER", colorName: "Teal", hex: "#14B8A6", layerBg: "bg-[#14B8A6]", layerTextClass: "text-white", usage: "调用方（买家）视角、搜索/发现、Caller 专属界面" },
  { layer: "RESPONDER", colorName: "Orange", hex: "#F97316", layerBg: "bg-[#F97316]", layerTextClass: "text-white", usage: "服务提供方（卖家）视角、发布/管理、Responder 专属界面" },
  { layer: "PLATFORM", colorName: "Purple", hex: "#8B5CF6", layerBg: "bg-[#8B5CF6]", layerTextClass: "text-white", usage: "官方托管平台、Marketplace、服务发现层、路由层" },
  { layer: "SELFHOST", colorName: "Yellow", hex: "#FACC15", layerBg: "bg-[#FACC15]", layerTextClass: "text-black", usage: "私有部署、企业内网扩展、私域数据隔离场景" },
  { layer: "HOTLINE", colorName: "Pink", hex: "#EC4899", layerBg: "bg-[#EC4899]", layerTextClass: "text-white", usage: "Hotline 能力标识、Marketplace 卡片、能力模板文档" },
];

export const accentColors: AccentColor[] = [
  { name: "Indigo", hex: "#6366F1" },
  { name: "Pink", hex: "#EC4899", note: "→ HOTLINE 产品层" },
  { name: "Red", hex: "#EF4444", note: "→ DESTRUCTIVE 优先" },
];
